import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { UploadSimple, CaretDown, CaretRight, UsersThree, ArrowsClockwise } from "@phosphor-icons/react";

function UploadNew({ clients, onUploaded }) {
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ title: "", doc_type: "rams", ref: "", revision: "", client_id: "", project_id: "",
    issue_date: "", review_date: "", ack_required: true, require_ack_from_workforce: false, ack_deadline: "", before_work: false, notes: "" });
  const [adv, setAdv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [preview, setPreview] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v, ...(k === "client_id" ? { project_id: "" } : {}) }));
  const jobs = clients.find((c) => c.id === f.client_id)?.jobs || [];

  const pickFile = (fl) => {
    if (!fl) return setFile(null);
    if (fl.type !== "application/pdf" && !fl.name.toLowerCase().endsWith(".pdf")) { toast.error("Please choose a PDF file"); return; }
    if (fl.size > 15 * 1024 * 1024) { toast.error("PDF is too large (max 15MB)"); return; }
    if (fl.size === 0) { toast.error("That file is empty"); return; }
    setFile(fl);
  };

  const submit = async () => {
    if (!file) return toast.error("Choose a PDF");
    if (!f.title.trim()) return toast.error("Title is required");
    if (f.require_ack_from_workforce && !f.client_id) return toast.error("Select a client to require acknowledgement from its workforce");
    setBusy(true); setDone(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(f).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
      const r = await api.post("/policies/rams/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(r.data.document);
      onUploaded?.();
      toast.success("RAMS/MS uploaded as a draft");
      if (r.data.assignment) {
        try {
          const pv = await api.get(`/policies/${r.data.document.id}/assignment-preview`);
          setPreview(pv.data);
        } catch { /* ignore */ }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-4 max-w-2xl" data-testid="rams-upload">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
        Upload a RAMS or Method Statement PDF — the PDF stays the authoritative content. It's stored privately as an <b>unassigned draft</b>. Choosing a client/job and ticking <b>require acknowledgement</b> creates the audience rule; the requirement only starts once you publish it and its effective date arrives. UK dates.
      </div>
      <label className="h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-emerald-400 text-slate-500 text-sm">
        <UploadSimple size={22} /><span>{file ? file.name : "Click to choose a PDF"}</span>
        <input data-testid="rams-file" type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} />
      </label>
      <Input data-testid="rams-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Title (e.g. Roof Works — Acme Site)" />
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-slate-600">Type
          <select data-testid="rams-type" value={f.doc_type} onChange={(e) => set("doc_type", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
            <option value="rams">RAMS</option><option value="risk_assessment">Risk Assessment</option><option value="method_statement">Method Statement</option><option value="toolbox_talk">Toolbox Talk</option>
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Issue / effective date
          <Input data-testid="rams-issue" type="date" value={f.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="mt-1" /></label>
        <label className="text-xs font-semibold text-slate-600">Client
          <select data-testid="rams-client" value={f.client_id} onChange={(e) => set("client_id", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
            <option value="">No client (tag only)</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></label>
        <label className="text-xs font-semibold text-slate-600">Job / site (optional)
          <select data-testid="rams-job" value={f.project_id} onChange={(e) => set("project_id", e.target.value)} disabled={!f.client_id} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm disabled:bg-slate-50">
            <option value="">Whole client (all jobs)</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select></label>
      </div>
      <label className="flex items-start gap-2 text-sm text-slate-700 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <input data-testid="rams-require-ack" type="checkbox" checked={f.require_ack_from_workforce} onChange={(e) => set("require_ack_from_workforce", e.target.checked)} className="mt-0.5" />
        <span>Require acknowledgement from people <b>actively assigned to this client{f.project_id ? "/job" : ""}</b>. {f.project_id ? "Only that job/site's team will be required." : "The whole active client workforce will be required."} Client tagging alone assigns nobody.</span>
      </label>

      <button onClick={() => setAdv(!adv)} className="flex items-center gap-1 text-sm font-semibold text-slate-500">{adv ? <CaretDown size={14} /> : <CaretRight size={14} />} Advanced settings</button>
      {adv && (
        <div className="grid sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3">
          <label className="text-xs font-semibold text-slate-600">Reference (auto if blank)<Input data-testid="rams-ref" value={f.ref} onChange={(e) => set("ref", e.target.value)} className="mt-1" /></label>
          <label className="text-xs font-semibold text-slate-600">Revision / version<Input value={f.revision} onChange={(e) => set("revision", e.target.value)} placeholder="1.0" className="mt-1" /></label>
          <label className="text-xs font-semibold text-slate-600">Review / expiry date<Input type="date" value={f.review_date} onChange={(e) => set("review_date", e.target.value)} className="mt-1" /></label>
          <label className="text-xs font-semibold text-slate-600">Acknowledgement deadline<Input type="date" value={f.ack_deadline} onChange={(e) => set("ack_deadline", e.target.value)} className="mt-1" /></label>
          <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2"><input data-testid="rams-before-work" type="checkbox" checked={f.before_work} onChange={(e) => set("before_work", e.target.checked)} /> Must be acknowledged before work</label>
          <label className="text-xs font-semibold text-slate-600 sm:col-span-2">Notes<Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 min-h-[60px]" /></label>
        </div>
      )}
      <div><Button data-testid="rams-upload-submit" disabled={busy || !file} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><UploadSimple size={15} /> {busy ? "Uploading…" : "Upload as draft"}</Button></div>
      {done && <div data-testid="rams-upload-done" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">Uploaded <b>{done.title}</b> ({done.ref}) as a draft. Open it in the Authoring tab to preview the PDF and publish (issue) it.</div>}
      {preview && (
        <div data-testid="rams-audience-preview" className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <div className="font-semibold text-slate-800 flex items-center gap-1.5 mb-1"><UsersThree size={16} /> Who this will apply to when issued</div>
          <div className="text-emerald-800 mb-1"><b>{preview.included_count}</b> included{preview.included_count > 0 ? ":" : "."}</div>
          <ul className="list-disc pl-5 text-slate-700">{preview.included.slice(0, 20).map((p) => <li key={p.profile_id}>{p.name} <span className="text-slate-400">— {p.sources?.join(", ")}</span></li>)}</ul>
          <div className="text-slate-500 mt-2">{preview.excluded_count} not included (not in this client/job workforce, or excepted).</div>
        </div>
      )}
    </div>
  );
}

function ReplaceRevision({ onDone }) {
  const [docs, setDocs] = useState([]);
  const [rid, setRid] = useState("");
  const [file, setFile] = useState(null);
  const [fresh, setFresh] = useState(true);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/policies?class=rams").then((r) => setDocs(r.data || [])).catch(() => {}); }, []);

  const pickFile = (fl) => {
    if (!fl) return setFile(null);
    if (fl.type !== "application/pdf" && !fl.name.toLowerCase().endsWith(".pdf")) { toast.error("Please choose a PDF file"); return; }
    if (fl.size > 15 * 1024 * 1024) { toast.error("PDF is too large (max 15MB)"); return; }
    if (fl.size === 0) { toast.error("That file is empty"); return; }
    setFile(fl);
  };

  const submit = async () => {
    if (!rid) return toast.error("Choose the RAMS to replace");
    if (!file) return toast.error("Choose the new PDF");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("requires_fresh_ack", String(fresh));
      if (reason) fd.append("change_summary", reason);
      const r = await api.post(`/policies/${rid}/revisions/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`New revision ${r.data.revision} created as a draft — publish it in Authoring to issue`);
      setFile(null); setReason(""); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create revision"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-3 max-w-2xl" data-testid="rams-replace">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
        Replace an issued RAMS/MS with a new PDF revision. The previous PDF and all its signatures are preserved. Choose whether the new version needs <b>fresh acknowledgement</b>; if not, prior acknowledgements carry forward and stay linked to the revision they signed. Publish the draft in Authoring to issue.
      </div>
      <label className="text-xs font-semibold text-slate-600">RAMS / Method Statement
        <select data-testid="replace-doc" value={rid} onChange={(e) => setRid(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
          <option value="">Choose…</option>
          {docs.map((d) => <option key={d.id} value={d.id}>{d.ref} — {d.title}</option>)}
        </select></label>
      <label className="h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-emerald-400 text-slate-500 text-sm">
        <UploadSimple size={20} /><span>{file ? file.name : "Choose new PDF"}</span>
        <input data-testid="replace-file" type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} />
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="replace-fresh" type="checkbox" checked={fresh} onChange={(e) => setFresh(e.target.checked)} /> Require fresh acknowledgement (uncheck for a minor correction — prior signatures carry forward)</label>
      <Textarea data-testid="replace-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the new revision (recorded in the audit)" className="min-h-[50px]" />
      <div><Button data-testid="replace-submit" disabled={busy} onClick={submit} className="bg-emerald-700 hover:bg-emerald-800 gap-1"><ArrowsClockwise size={15} /> {busy ? "Uploading…" : "Create new revision (draft)"}</Button></div>
    </div>
  );
}

export default function PolicyRamsUpload() {
  const [clients, setClients] = useState([]);
  const [mode, setMode] = useState("new");
  const [nonce, setNonce] = useState(0);
  useEffect(() => { api.get("/workforce/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <Button data-testid="rams-mode-new" variant={mode === "new" ? "default" : "outline"} onClick={() => setMode("new")} className={mode === "new" ? "bg-emerald-700 hover:bg-emerald-800" : ""}>Upload new</Button>
        <Button data-testid="rams-mode-replace" variant={mode === "replace" ? "default" : "outline"} onClick={() => setMode("replace")} className={mode === "replace" ? "bg-emerald-700 hover:bg-emerald-800" : ""}>Replace with new revision</Button>
      </div>
      {mode === "new" ? <UploadNew clients={clients} onUploaded={() => setNonce((n) => n + 1)} /> : <ReplaceRevision key={nonce} onDone={() => setNonce((n) => n + 1)} />}
    </div>
  );
}
